/**
 * React 绑定：I18nProvider / useI18n。
 * - 首次渲染前按 PRD §8.4 识别语言并懒加载词条（当前语言 + 兜底链，全部命名空间合并）；
 * - 切换语言立即生效、不刷新页面（PRD 4.1「切换立即生效，不刷新页面，已填内容保留」）；
 * - 运行时词条包（A45）到达或更新时重新渲染，≤5 分钟生效（PRD 5.14 A45）；
 * - 同步 <html lang>，供字体栈与读屏使用。
 *
 * 用法：
 * - 应用：`<I18nProvider initialLang={lang} surface="user">`（内置种子 + 运行时词条包 + 缺译上报，永不显示词条编号）；
 * - 自行组装：`<I18nProvider app={createAppI18n(...)}>`；
 * - 骨架写法 `<I18nProvider initialLang loader namespaces>` 仍然可用（不连接运行时词条包）。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { NumberSymbols } from '@nexora/shared';
import { createAppI18n, type AppI18n, type AppI18nRemoteOptions } from './app.js';
import { numberSymbols } from './format.js';
import type { FallbackItem, Lang } from './langs.js';
import { createI18n, type I18n, type MessageLoader, type MessageVars } from './runtime.js';
import type { Surface } from './surface.js';

export { createAppI18n, type AppI18n, type AppI18nOptions, type AppI18nRemoteOptions } from './app.js';

export interface I18nContextValue {
  i18n: I18n;
  lang: Lang;
  t: (key: string, vars?: MessageVars) => string;
  setLang: (lang: Lang) => Promise<void>;
  symbols: NumberSymbols;
  /** 词条版本（每次词条变化加 1） */
  revision: number;
  /** ws `system.status` 推送的 i18nV：比当前词条包新时立即拉取（未连接运行时词条包时无操作） */
  syncVersion: (v: number) => Promise<void>;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  /** 初始语言（调用方用 detectFromBrowser 等识别得到）；传 app 时可省略 */
  initialLang?: Lang;
  /** 已组装好的应用 i18n（createAppI18n） */
  app?: AppI18n;
  /** 按使用端组装（等同 createAppI18n({ surface })）；与 loader 二选一 */
  surface?: Surface;
  /** 运行时词条包选项（surface 模式下有效） */
  remote?: AppI18nRemoteOptions | boolean;
  fallbackOrder?: readonly FallbackItem[];
  param?: (key: string) => string | null | undefined;
  /** 骨架写法：自定义加载器 */
  loader?: MessageLoader;
  namespaces?: readonly string[];
  onMissing?: (key: string, lang: Lang) => void;
  /** 词条加载完成前显示的内容 */
  fallback?: ReactNode;
  children: ReactNode;
}

function buildApp(props: I18nProviderProps): AppI18n {
  if (props.app) return props.app;
  const lang = props.initialLang;
  if (!lang) throw new Error('I18nProvider needs initialLang or app');
  if (props.surface) {
    return createAppI18n({
      lang,
      surface: props.surface,
      ...(props.loader ? { loader: props.loader } : {}),
      ...(props.namespaces ? { namespaces: props.namespaces } : {}),
      ...(props.fallbackOrder ? { fallbackOrder: props.fallbackOrder } : {}),
      ...(props.param ? { param: props.param } : {}),
      ...(props.remote !== undefined ? { remote: props.remote } : {}),
    });
  }
  if (!props.loader) throw new Error('I18nProvider needs a loader, a surface or an app');
  const i18n = createI18n({
    lang,
    loader: props.loader,
    ...(props.namespaces ? { namespaces: props.namespaces } : {}),
    ...(props.onMissing ? { onMissing: props.onMissing } : {}),
    ...(props.fallbackOrder ? { fallbackOrder: props.fallbackOrder } : {}),
    ...(props.param ? { param: props.param } : {}),
    // 永不显示词条编号（PRD 5.14 A45）
    missing: 'empty',
  });
  return { i18n, sync: null, misses: null, start: () => () => undefined };
}

export function I18nProvider(props: I18nProviderProps) {
  const { fallback = null, children } = props;
  const [app] = useState(() => buildApp(props));
  const { i18n } = app;
  const [lang, setLangState] = useState<Lang>(i18n.lang);
  const [revision, setRevision] = useState(i18n.revision);
  const [ready, setReady] = useState(() => i18n.isLoaded());

  useEffect(() => {
    let active = true;
    const unsubscribe = i18n.subscribe((next) => setLangState(next));
    const unwatch = i18n.onChange(() => setRevision(i18n.revision));
    i18n.load().then(
      () => active && setReady(true),
      () => active && setReady(true),
    );
    return () => {
      active = false;
      unsubscribe();
      unwatch();
    };
  }, [i18n]);

  // 挂载时开始同步运行时词条包，卸载时停止（StrictMode 下重复挂载也安全）
  useEffect(() => app.start(), [app]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => i18n.setLang(next), [i18n]);
  // lang 或词条变化时重建 t，让使用方重新渲染
  const t = useCallback((key: string, vars?: MessageVars) => i18n.t(key, vars), [i18n, lang, revision]);
  const syncVersion = useCallback(async (v: number) => {
    await app.sync?.notifyVersion(v);
  }, [app]);
  const value = useMemo<I18nContextValue>(
    () => ({ i18n, lang, t, setLang, symbols: numberSymbols(lang), revision, syncVersion }),
    [i18n, lang, t, setLang, revision, syncVersion],
  );

  if (!ready) return <>{fallback}</>;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}
